import React, { PureComponent } from 'react';
import { arrayOf, bool, string, func, shape } from 'prop-types';
import { Grid } from '@mui/material';
import { Dialog, DialogContent } from '@mui/material';
import { CircularProgress } from '@mui/material';
import TemplatesList from 'components/TemplatesList'; // eslint-disable-line
import { NotificationForm } from 'components/Notifications';
import DialogHeader from './DialogHeader';
import DialogControls from './DialogControls';

const classPrefixesOfColumns = {
  four: 4,
  eight: 8,
  twelve: 12,
};

class SendNoticeDialog extends PureComponent {
  static propTypes = {
    hideSendNoticeModal: func,
    isOpen: bool, // eslint-disable-line
    routes: arrayOf(
      shape({
        active: bool,
        busId: string,
        campus: string,
        id: string,
        inAlarm: bool,
        name: string,
        needAttention: bool,
        number: string,
        online: bool,
        state: string,
      }),
    ),
    toRoute: bool,
    // task 09: optional prefill payload from the RouteDetails banner Cancel
    // button. When non-null, overrides selectedTemplate as the `template` prop
    // passed to <NotificationForm> and supplies prefilledDiversionNotificationId.
    prefilledDiversion: shape({
      body: string,
      header: string,
      type: string,
      diversionNotificationId: string,
    }),
    templates: shape({
      list: arrayOf(
        shape({
          id: string,
          header: string,
          body: string,
          type: string,
        }),
      ),
    }),
    isModalLoading: bool.isRequired,
  };

  static defaultProps = {
    hideSendNoticeModal: () => {},
    isOpen: false,
    routes: [],
    templates: {},
    toRoute: false,
    prefilledDiversion: null,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const { isOpen: nextIsOpen } = nextProps;
    const { openState: prevOpenState } = prevState;

    if (nextIsOpen && !prevOpenState) {
      return {
        ...prevState,
        openState: nextIsOpen,
        selectedTemplate: {},
      };
    }

    return {
      ...prevState,
      openState: nextIsOpen,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      openState: false,
      selectedTemplate: {},
      templatesVisible: true,
    };
  }

  onSelectedTemplateChange = selectedTemplateId => {
    const { templates } = this.props;

    this.setState({
      selectedTemplate: templates.list.find(template => template.id === selectedTemplateId),
    });
  };

  handleTemplatesVisibilityChange = () => {
    const { templatesVisible } = this.state;

    this.setState({ templatesVisible: !templatesVisible });
  };

  handleClose = () => {
    const { isModalLoading, hideSendNoticeModal } = this.props;

    // Close via the redux source of truth. Setting only local `openState`
    // is futile — getDerivedStateFromProps re-derives openState from the
    // `isOpen` prop on every render, so a local-only close is clobbered back
    // open. Hiding through redux flips `isOpen`, which gDSFP maps to
    // openState=false. (The old `onExited={hideSendNoticeModal}` never fired:
    // it's a dead prop in MUI 7 — the callback moved to slotProps.transition.)
    if (!isModalLoading) {
      hideSendNoticeModal();
    }
  };

  render() {
    const { routes, toRoute, isModalLoading, prefilledDiversion } = this.props;
    const { openState, templatesVisible, selectedTemplate } = this.state;

    const spacing = templatesVisible ? classPrefixesOfColumns.eight : classPrefixesOfColumns.twelve;

    return (
      <Dialog
        open={openState}
        onClose={this.handleClose}
        aria-labelledby="form-dialog-title"
        aria-hidden="false"
        className="send-notice-dialog"
        fullWidth
        PaperProps={{ style: { width: '80%', maxWidth: '10000px' } }}
      >
        <DialogHeader routes={routes} onClose={this.handleClose} toRoute={false} />
        <DialogControls
          onVisibilityChange={this.handleTemplatesVisibilityChange}
          templatesVisible={templatesVisible}
        />
        <DialogContent sx={{ borderTop: 1, borderBottom: 1, borderColor: 'divider', p: 0 }}>
          <Grid container spacing={0}>
            <Grid
              className="templates-list-grid"
              sx={{ borderRight: 1, borderColor: 'divider' }}
              size={{
                xs: classPrefixesOfColumns.twelve,
                sm: classPrefixesOfColumns.twelve,
                md: classPrefixesOfColumns.four,
                lg: classPrefixesOfColumns.four,
                xl: classPrefixesOfColumns.four
              }}>
              <TemplatesList
                onSelectedTemplateChange={this.onSelectedTemplateChange}
                open={templatesVisible}
              />
            </Grid>
            <Grid
              className="notice-form-grid"
              size={{
                xs: classPrefixesOfColumns.twelve,
                sm: classPrefixesOfColumns.twelve,
                md: spacing,
                lg: spacing,
                xl: spacing
              }}>
              <NotificationForm
                routes={routes}
                // prefilledDiversion (set by RouteDetails cancel-banner) overrides any selectedTemplate
                // the user picks from the panel; v1 accepts this UX gap per spec.
                template={prefilledDiversion || selectedTemplate}
                prefilledDiversionNotificationId={
                  prefilledDiversion ? prefilledDiversion.diversionNotificationId : undefined
                }
                toRoute={toRoute}
              />
            </Grid>
          </Grid>
        </DialogContent>
        {isModalLoading && (
          <div className="loader">
            <CircularProgress size={50} />
          </div>
        )}
      </Dialog>
    );
  }
}

export default SendNoticeDialog;
